/**
 * §6.7 — what this business owes.
 *
 * THE MIRROR OF THE INVOICES SCREEN, and deliberately the same shape: the
 * operator reads one of these the way they read the other, and a payables
 * screen that invented its own layout would be a second thing to learn for a
 * list that answers the same question pointing the other way.
 *
 * WHY IT EXISTS BEFORE ANYBODY USES IT
 *
 * The operator asked for it knowing they have no bills recorded yet ("I would
 * lean towards building it even though I would not use it for now"). The
 * forecast is the reason it is worth having now: a bill with a due date is one
 * of the four things that can be said with certainty about upcoming cash, and
 * a forecast that silently omits payables is one that reads as complete while
 * missing a whole side of the books.
 *
 * WHAT IS NOT HERE, AND IT IS A REAL GAP
 *
 * Bill-payment MATCHING. The machinery exists -- `listPaymentsMade` is the
 * other half of the read, and `POST /clearings` is role-agnostic because the
 * triggers decide what is legal, so allocating a payment to a bill needs no
 * server work at all. What is missing is a screen, and it would be several
 * hundred lines mirroring Matching for a workflow with no data in it. It is a
 * screen away on the day the first bill is paid, and stating that is better
 * than building something nobody can exercise.
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type OpenBill, type OpenBillsResult } from '../api.js';
import { formatCents } from '../lib/money.js';

export function BillsPage() {
  const [bills, setBills] = useState<OpenBillsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .listOpenBills({ counterparty: search.trim() || undefined })
      .then((result) => {
        if (cancelled) return;
        setBills(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not load the bills.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [search]);

  /**
   * How late a bill is, in words, or nothing when nobody recorded terms.
   *
   * Migration 0036's rule on screen: no due date is not "due today" and not
   * "not due". It is unknown, and the column stays empty rather than saying
   * something the books cannot support.
   */
  function lateness(bill: OpenBill): string {
    if (bill.daysOverdue === null) return '';
    if (bill.daysOverdue > 0) return `${bill.daysOverdue} days late`;
    if (bill.daysOverdue === 0) return 'due today';
    return `in ${Math.abs(bill.daysOverdue)} days`;
  }

  return (
    <section className="invoices">
      <header className="row reports-head">
        <h1>Bills</h1>
        <span className="muted">
          {bills === null ? '' : `${formatCents(bills.totalOutstandingCents)} owed`}
        </span>
      </header>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <p className="muted">
        Posted bills with money still owed. A bill is an entry that credits Accounts
        Payable, the mirror of an invoice. Nothing here is paid by this application.
      </p>

      <div className="row triage-filters">
        <label>
          Search{' '}
          <input
            type="search"
            aria-label="Search bills"
            placeholder="who it is owed to"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      {bills === null ? (
        !error && <p className="muted">Loading…</p>
      ) : bills.bills.length === 0 ? (
        <p className="muted">
          Nothing owed. A bill appears here once it is POSTED and has money left on its
          payable line.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="lines invoice-list">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Owed To</th>
                <th>Date</th>
                <th>Due</th>
                <th className="num">Amount</th>
                <th className="num">Paid</th>
                <th className="num">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {bills.bills.map((bill) => (
                <tr key={bill.detailId}>
                  <td>{bill.invoiceReference ?? 'No Reference'}</td>
                  <td>{bill.counterparty ?? '—'}</td>
                  <td className="date-cell">{bill.entryDate}</td>
                  <td className="due-cell">
                    {bill.dueDate ?? <span className="muted">no terms</span>}
                    {/*
                      `lateness` decides on its own whether there is anything to
                      say, rather than being guarded here as well. It was
                      guarded in both places, which made the function's own null
                      branch unreachable -- a mutation that changed it to "due
                      today" broke nothing, because nothing ever called it that
                      way. One guard, in the function, and the test reaches it.
                    */}
                    {lateness(bill) !== '' && (
                      <span className="muted"> · {lateness(bill)}</span>
                    )}
                  </td>
                  <td className="num">{formatCents(bill.originalCents)}</td>
                  <td className="num">
                    {bill.clearedCents > 0 ? formatCents(bill.clearedCents) : ''}
                  </td>
                  <td className="num">{formatCents(bill.outstandingCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bills !== null && bills.total > bills.bills.length && (
        <p className="notice notice-warn">
          Showing {bills.bills.length} of {bills.total}. Search to find an older one.
        </p>
      )}
    </section>
  );
}
